import React, { useState } from 'react';
import { Grid, createStyles, makeStyles } from '@material-ui/core';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { useParams } from 'react-router-dom';
import { CATEGORIES_TREE } from '../../../gql/categories';
import { GET_BROWSE_STAT_WITH_SUBSCIPTION } from '../../../gql/articles';
import { DesksSection } from '../../../components/ui/DesksSection';
import { ExtendedTheme, Loader, Breadcrumbs, BreadcrumbsText, Link, ArrowsChevronLeft } from 'neon-design-system';
import { ArticleLinkList } from '../../../components/ui/ArticleLinkList';
import Archives from '../../ui/Archives';
import { CategoryItemNode, BrowseArticleStat } from '../../../shared/types';
import { getCategoryItemFromTree } from '../../../utils/list-utils';
import BrowseCategoryList from '../../ui/BrowseCategoryList';
import CategoryCover from '../../ui/CategoryCover';
import { BrowseArticleList } from './BrowseArticleList';
import ArticleTagList from '../../ui/ArticleTagList';
import { useActivityLogger } from '../../hooks/use-activity-logger';
import { useWaitForNumCalls } from '../../hooks/use-wait-for-num-calls';
import { useUser } from '../../providers/UserProvider';
import { CHANGE_SUBSCRIPTION } from '../../../gql/subscriptions';
import ManageTemplateDialog from '../../ui/ManageTemplateDialog';
import { baseUrls } from '../../../utils/baseUrls.utils';
import PremiumChannelList from '../../ui/PremiumChannelList';

interface BrowseCategoryPageBreadCrumbsProps {
	browseItem: CategoryItemNode;
	categories: CategoryItemNode[];
	showOnlyFirstParent?: boolean;
}

const useStyles = makeStyles<ExtendedTheme>(theme => createStyles({
	root: {
		[theme.breakpoints.only('md')]: {
			marginTop: theme.spacing(1)
		}
	},
	breadCrumbsWrapper: {
		[theme.breakpoints.down('sm')]: {
			display: 'none'
		},
		'& > nav': {
			borderBottom: 'none',
			marginTop: `-${theme.spacing(2)}px`
		}
	},
	breadCrumbsWrapperForSmallScreens: {
		display: 'none',
		'& > nav': {
			display: 'flex',
			borderBottom: 'none',
			paddingBottom: theme.spacing(2),
			'& > a': {
				display: 'flex',
				alignItems: 'center',
				fontWeight: 'bold',
				'& > svg': {
					width: 10,
					marginRight: theme.spacing(1)
				}
			}
		},
		[theme.breakpoints.down('sm')]: {
			display: 'block'
		}
	},
	sectorCardList: {
		marginBottom: theme.spacing(4)
	},
	sidebar: {
		[theme.breakpoints.down('sm')]: {
			marginTop: theme.spacing(4)
		},
		[theme.breakpoints.up('md')]: {
			paddingLeft: theme.spacing(5) + 'px !important'
		},
		[theme.breakpoints.up('lg')]: {
			paddingLeft: theme.spacing(9) + 'px !important'
		},
		'& > div': {
			marginBottom: theme.spacing(6)
		}
	},
	linkEmail: {
		fontSize: theme.typography.body1.fontSize
	}
}));

const getParentsListById = (categories: CategoryItemNode[], parentId: string | undefined, parentsList: CategoryItemNode[]) => {
	const categoryFromTree = getCategoryItemFromTree(categories, parentId);

	if (categoryFromTree) {
		parentsList.push(categoryFromTree);
		getParentsListById(categories, categoryFromTree.parent, parentsList);
	}
};

const BrowseCategoryPageBreadCrumbs = ({ browseItem, categories, showOnlyFirstParent = false }: BrowseCategoryPageBreadCrumbsProps): JSX.Element => {
	const parentsList: CategoryItemNode[] = [];
	getParentsListById(categories, browseItem.parent, parentsList)
	parentsList.reverse();

	if (showOnlyFirstParent) {
		const firstParent = parentsList[parentsList.length - 1];
		return (
			<nav>
				<Link to={!firstParent ? baseUrls.browse.main : `${baseUrls.browse.main}/${firstParent._id}`}>
					<ArrowsChevronLeft />
					{!firstParent ? 'Back to Browse' : `Back to ${firstParent.name}`}
				</Link>
			</nav>
		);
	}

	const breadCrumbsLinks = parentsList.map((parentCategory: CategoryItemNode) => (
		<Link to={`${baseUrls.browse.main}/${parentCategory._id}`} key={parentCategory._id}>{parentCategory.name}</Link>
	));

	breadCrumbsLinks.unshift(<Link to={baseUrls.browse.main} key="all-publications">All publications</Link>);
	breadCrumbsLinks.push(<BreadcrumbsText key={browseItem._id}>{browseItem.name}</BreadcrumbsText>)

	if (parentsList.length) {
		return (
			<Breadcrumbs>
				{breadCrumbsLinks}
			</Breadcrumbs>
		);
	}

	return (
		<Breadcrumbs>
			<Link to={baseUrls.browse.main}>All publications</Link>
			<BreadcrumbsText>{browseItem.name}</BreadcrumbsText>
		</Breadcrumbs>
	)
};

const BrowseCategoryPage: React.FC = () => {
	const classes = useStyles();
	const { categoryId } = useParams<{categoryId: string}>();
	const { userDetails } = useUser();
	const [confirmationDialogOpened, setConfirmationDialogOpened] = useState<boolean>(false);

	const queryCategoryTree = useQuery<{ categoriesAsTree: CategoryItemNode[] }>(CATEGORIES_TREE, { fetchPolicy: 'network-only' });

	const treeData = queryCategoryTree.data?.categoriesAsTree ?? [];
	const browseItem = getCategoryItemFromTree(treeData, categoryId);

	const pageLoadComplete = useActivityLogger('Browse', browseItem?.name, categoryId, true);

	const componentLoaded = useWaitForNumCalls(3, pageLoadComplete);
	const queryStat = useQuery<{ getBrowseStat: BrowseArticleStat; isSubscribedTo?: boolean }>(GET_BROWSE_STAT_WITH_SUBSCIPTION, {
		fetchPolicy: 'network-only',
		variables: { categoryId },
		onCompleted: componentLoaded
	});

	const [changeSubscription] = useMutation(CHANGE_SUBSCRIPTION, {
		refetchQueries: [{ query: GET_BROWSE_STAT_WITH_SUBSCIPTION, variables: { categoryId } }],
		awaitRefetchQueries: true
	});

	if (queryCategoryTree.loading || queryStat.loading || !queryStat.data) return <Loader />;

	if (!browseItem) {
		throw new Error(`Cannot find category with id ${categoryId}`);
	}
	const isSubscribedToCategory: boolean = queryStat?.data?.isSubscribedTo ?? false;

	const desksToShow = queryStat.data.getBrowseStat?.desks;
	const tagsToShow = queryStat.data.getBrowseStat?.tags;
	const trendingArticles = queryStat.data.getBrowseStat?.trendingArticles;

	function modifySubscription() {
		if (!isSubscribedToCategory) {
			doModifySubscription();
		} else {
			setConfirmationDialogOpened(true);
		}
	}

	function doModifySubscription() {
		changeSubscription({
			variables: {
				item: {
					userId: userDetails?.id,
					entityId: categoryId,
					entityType: 'Category',
					name: browseItem?.name,
					isAdd: !isSubscribedToCategory
				}
			}
		});
	}

	return (
		<Grid container spacing={2} className={classes.root}>
			<Grid item xs={12}>
				<div className={classes.breadCrumbsWrapper}>
					<BrowseCategoryPageBreadCrumbs
						categories={treeData}
						browseItem={browseItem}
					/>
				</div>
				<div className={classes.breadCrumbsWrapperForSmallScreens}>
					<BrowseCategoryPageBreadCrumbs
						categories={treeData}
						browseItem={browseItem}
						showOnlyFirstParent
					/>
				</div>
				<CategoryCover
					name={browseItem.name}
					id={browseItem._id}
					description={browseItem.description}
					imageUrl={browseItem.largeImage}
					isSubscribed={isSubscribedToCategory}
					modifySubscription={modifySubscription}
					categories={browseItem.children.map((category) => ({
						id: category._id,
						name: category.name,
						url: `${baseUrls.browse.main}/${category._id}`
					}))}
				/>
			</Grid>
			<Grid item xs={12} md={8}>
				<BrowseArticleList title="Latest articles" categoryId={categoryId} showPlacement={true} onDataLoaded={componentLoaded} />
				<ManageTemplateDialog
					title="Are you sure you want to unfollow?"
					dialogMessage={<>You are about to unfollow {browseItem?.name}.
						<br></br><br></br>
						If you unfollow you will no longer see related articles on your For You section, and you won’t receive email notifications in your inbox.
						<br></br><br></br>
						If you prefer, you can <Link to={baseUrls.emailNotifications} className={classes.linkEmail}>manage email notifications</Link>.
					</>}
					submitBtnText="Yes, unfollow"
					cancelBtnText="Cancel"
					dialogOpenState={confirmationDialogOpened}
					onDelete={() => {
						doModifySubscription();
						setConfirmationDialogOpened(false);
					}}
					submitBtnColorType="primary"
					closeDialog={() => setConfirmationDialogOpened(false)}
				/>
			</Grid>
			<Grid className={classes.sidebar} item xs={12} md={4}>
				<BrowseCategoryList categories={browseItem.children} parentCategoryName={browseItem.name} onDataLoaded={componentLoaded} />
				<ArticleLinkList title={`Trending in ${browseItem.name}`} articles={trendingArticles} />
				<PremiumChannelList maxLength={25} />
				<ArticleTagList showTwoRowsOnly tags={tagsToShow} />
				<DesksSection deskList={desksToShow} />
				<Archives />
			</Grid>
		</Grid>
	);
}

export default BrowseCategoryPage;

